/**
 * DrumChallenges – ten call-and-response rhythm challenges for each of the
 * twelve African instruments.
 *
 * The page listens first: it plays a phrase on the instrument's pads, counts
 * you in, then judges you playing it back. Open to everyone; a signed-in
 * player also gets their run saved to the drum leaderboard.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AFRICAN_INSTRUMENTS,
  ALL_SAMPLE_URLS,
  AfricanInstrument,
} from '../../../data/africanInstruments';
import { useSamplePlayer } from '../../../hooks/useSamplePlayer';
import { usePageLoadAnimation } from '../../../hooks/useAnimation';
import { useAuth } from '../../../context/AuthContext';
import { DrumChallenge, getChallenges } from '../data/challenges';
import { useDrumChallenge, DrumRunStats } from '../hooks/useDrumChallenge';
import {
  accuracyToGrade,
  overallAccuracy,
  DrumHitRating,
} from '../lib/scoring';
import { GRADE_COLORS, NEUTRAL } from '../lib/theme';
import {
  DrumProgressEntry,
  fetchDrumProgress,
  submitDrumScore,
} from '../api';
import { DrumPad } from '../components/DrumPad';
import { DrumNoteHighway } from '../components/DrumNoteHighway';

type SaveState = 'idle' | 'saving' | 'saved' | 'failed' | 'signed-out';

export const DrumChallenges: React.FC = () => {
  usePageLoadAnimation();
  const { user } = useAuth();

  const { load, play, isReady, isLoading, loadedCount, totalCount, error } =
    useSamplePlayer(ALL_SAMPLE_URLS);

  const [instrument, setInstrument] = useState<AfricanInstrument | null>(null);
  const [challenge, setChallenge] = useState<DrumChallenge | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [result, setResult] = useState<
    (DrumRunStats & { accuracy: number; grade: string }) | null
  >(null);
  const [progress, setProgress] = useState<Map<string, DrumProgressEntry>>(
    new Map(),
  );
  const [struckPad, setStruckPad] = useState<number | null>(null);

  const challenges = useMemo(
    () => (instrument ? getChallenges(instrument.slug) : []),
    [instrument],
  );

  // Personal bests drive the "cleared" markers. Signed-out visitors just see
  // none, which is why a failure here is quietly ignored.
  const refreshProgress = useCallback(async () => {
    if (!user) {
      setProgress(new Map());
      return;
    }
    try {
      const rows = await fetchDrumProgress();
      setProgress(new Map(rows.map((row) => [row.challenge_id, row])));
    } catch {
      setProgress(new Map());
    }
  }, [user]);

  useEffect(() => {
    void refreshProgress();
  }, [refreshProgress]);

  const playPad = useCallback(
    (padIndex: number) => {
      const pad = instrument?.pads[padIndex];
      if (pad) play(pad.src, pad.pitchRatio);
    },
    [instrument, play],
  );

  const handleFinish = useCallback(
    (stats: DrumRunStats) => {
      const accuracy = overallAccuracy(
        stats.perfectCount,
        stats.goodCount,
        stats.missCount,
      );
      const grade = accuracyToGrade(accuracy);
      setResult({ ...stats, accuracy, grade });

      if (!user) {
        setSaveState('signed-out');
        return;
      }
      if (!challenge || !instrument) return;

      // Saving is best-effort: the player has already earned the result on
      // screen, and a network failure must not take it away from them.
      setSaveState('saving');
      void submitDrumScore(challenge.id, {
        instrumentSlug: instrument.slug,
        score: stats.score,
        accuracy,
        maxCombo: stats.maxCombo,
        perfectCount: stats.perfectCount,
        goodCount: stats.goodCount,
        missCount: stats.missCount,
        totalNotes:
          stats.perfectCount + stats.goodCount + stats.missCount,
        grade,
      })
        .then(() => {
          setSaveState('saved');
          void refreshProgress();
        })
        .catch(() => setSaveState('failed'));
    },
    [challenge, instrument, refreshProgress, user],
  );

  const game = useDrumChallenge({ challenge, playPad, onFinish: handleFinish });
  const { phase, hit, start, reset, lastJudgement } = game;

  // Flash the pad the player just struck, whatever the rating.
  useEffect(() => {
    if (!lastJudgement) return;
    setStruckPad(lastJudgement.padIndex);
    const timer = window.setTimeout(() => setStruckPad(null), 160);
    return () => window.clearTimeout(timer);
  }, [lastJudgement]);

  const beginRun = useCallback(async () => {
    if (!isReady) await load();
    setResult(null);
    setSaveState('idle');
    start();
  }, [isReady, load, start]);

  const openChallenge = useCallback(
    (next: DrumChallenge) => {
      reset();
      setResult(null);
      setSaveState('idle');
      setChallenge(next);
      // Decode up front so the demo does not stall on first play.
      if (!isReady) void load();
    },
    [isReady, load, reset],
  );

  // Keyboard play. Bound only while a challenge is open, so the picker keeps
  // normal keyboard behaviour.
  useEffect(() => {
    if (!instrument || !challenge) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.repeat || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;

      const padIndex = instrument.pads.findIndex(
        (pad) => pad.keyBinding === event.key.toLowerCase(),
      );
      if (padIndex === -1) return;

      event.preventDefault();
      hit(padIndex);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [challenge, hit, instrument]);

  // ---------------------------------------------------------------- picker
  if (!instrument) {
    return (
      <div className="min-h-screen bg-studio-950">
        <PageHeader />
        <section className="px-4 sm:px-8 lg:px-16 pb-24">
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {AFRICAN_INSTRUMENTS.map((item) => {
              const cleared = getChallenges(item.slug).filter((c) =>
                progress.has(c.id),
              ).length;

              return (
                <button
                  key={item.slug}
                  type="button"
                  onClick={() => setInstrument(item)}
                  className="text-left flex flex-col bg-studio-800 border border-studio-700 rounded-2xl overflow-hidden shadow-card hover:shadow-card-hover hover:border-amber-500/50 transition-all duration-300"
                >
                  <img
                    src={item.image}
                    alt={item.nameEn}
                    loading="lazy"
                    className="h-40 w-full object-cover"
                  />
                  <div className="p-5 flex-1 flex flex-col">
                    <h2 className="font-display text-xl font-bold text-stone-100">
                      {item.nameEn}
                    </h2>
                    <p className="text-stone-400 text-sm">{item.nameZh}</p>
                    <p className="text-stone-500 text-xs mt-2 flex-1">
                      {item.originEn}
                    </p>
                    <div className="flex items-center justify-between mt-4">
                      <span className="text-stone-500 text-xs font-mono">
                        {item.pads.length} pads
                      </span>
                      <span className="text-amber-500 text-xs font-semibold">
                        {user ? `${cleared}/10 played` : '10 challenges'}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      </div>
    );
  }

  // ------------------------------------------------------------ challenge list
  if (!challenge) {
    return (
      <div className="min-h-screen bg-studio-950">
        <PageHeader />
        <section className="px-4 sm:px-8 lg:px-16 pb-24">
          <div className="max-w-4xl mx-auto">
            <button
              type="button"
              onClick={() => setInstrument(null)}
              className="text-stone-500 hover:text-amber-500 text-sm mb-6 transition-colors"
            >
              ← All instruments
            </button>

            <div className="flex items-center gap-5 mb-8">
              <img
                src={instrument.image}
                alt={instrument.nameEn}
                className="w-20 h-20 rounded-xl object-cover border border-studio-700"
              />
              <div>
                <h2 className="font-display text-3xl font-bold text-stone-100">
                  {instrument.nameEn}
                </h2>
                <p className="text-stone-400">{instrument.nameZh}</p>
              </div>
            </div>

            <div className="space-y-3">
              {challenges.map((item) => {
                const best = progress.get(item.id);
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => openChallenge(item)}
                    className="w-full flex items-center gap-4 bg-studio-900/50 border border-studio-800 hover:border-amber-500/50 rounded-xl p-4 transition-all text-left"
                  >
                    <span className="w-10 h-10 shrink-0 rounded-lg bg-studio-800 text-stone-400 flex items-center justify-center font-display font-bold">
                      {item.index}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-stone-100 font-semibold">
                        {item.name}
                      </span>
                      <span className="block text-stone-500 text-xs font-mono">
                        {item.bpm} BPM · {item.steps.length} strokes
                      </span>
                    </span>
                    {best && (
                      <span className="text-right">
                        <span className="block font-display text-lg font-bold text-stone-100">
                          {best.best_score.toLocaleString()}
                        </span>
                        <span
                          className="block text-xs font-bold"
                          style={{ color: GRADE_COLORS[best.best_grade] ?? NEUTRAL }}
                        >
                          {best.best_grade}
                        </span>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      </div>
    );
  }

  // ------------------------------------------------------------------ playing
  const promptIndex = game.promptPadIndex;
  const ratingByPad: DrumHitRating | null =
    lastJudgement && struckPad === lastJudgement.padIndex
      ? lastJudgement.rating
      : null;

  return (
    <div className="min-h-screen bg-studio-950">
      <PageHeader />
      <section className="px-4 sm:px-8 lg:px-16 pb-24">
        <div className="max-w-3xl mx-auto">
          <button
            type="button"
            onClick={() => {
              reset();
              setChallenge(null);
              setResult(null);
            }}
            className="text-stone-500 hover:text-amber-500 text-sm mb-6 transition-colors"
          >
            ← {instrument.nameEn} challenges
          </button>

          <div className="bg-studio-900/50 border border-studio-800 rounded-2xl p-6 mb-6">
            <div className="flex items-baseline justify-between mb-1">
              <h2 className="font-display text-2xl font-bold text-stone-100">
                {challenge.index}. {challenge.name}
              </h2>
              <span className="font-mono text-stone-500 text-sm">
                {challenge.bpm} BPM
              </span>
            </div>
            <p className="text-stone-500 text-sm">
              {instrument.nameEn} · {challenge.steps.length} strokes
            </p>

            <div className="flex flex-wrap items-center gap-6 mt-5">
              <Stat label="Score" value={game.score.toLocaleString()} />
              <Stat label="Combo" value={`${game.combo}x`} />
              <Stat
                label="Progress"
                value={`${game.judgedCount}/${game.totalSteps}`}
              />
            </div>
          </div>

          <StatusBanner
            phase={phase}
            countdownBeat={game.countdownBeat}
            isLoading={isLoading}
            loadedCount={loadedCount}
            totalCount={totalCount}
            error={error}
          />

          {/* Notes fall toward the line — during the demo to show the phrase,
              and again on the player's turn so it need not be memorised. */}
          <DrumNoteHighway
            pads={instrument.pads}
            steps={challenge.steps}
            phraseStartMs={game.phraseStartMs}
            isDemo={phase === 'listening'}
            lastHit={lastJudgement}
          />

          <div
            className="grid gap-3 my-6"
            style={{
              gridTemplateColumns: `repeat(${Math.min(
                instrument.pads.length,
                4,
              )}, minmax(0, 1fr))`,
            }}
          >
            {instrument.pads.map((pad, index) => (
              <DrumPad
                key={pad.keyBinding}
                pad={pad}
                isPrompting={promptIndex === index}
                isStruck={struckPad === index}
                rating={struckPad === index ? ratingByPad : null}
                disabled={phase === 'listening' || phase === 'countdown'}
                onHit={() => hit(index)}
              />
            ))}
          </div>

          {result ? (
            <ResultPanel
              result={result}
              saveState={saveState}
              onRetry={() => void beginRun()}
              challengeId={challenge.id}
            />
          ) : (
            <button
              type="button"
              onClick={() => void beginRun()}
              disabled={phase === 'listening' || phase === 'countdown' || phase === 'playing'}
              className="btn-primary w-full disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {phase === 'idle' ? 'Listen & Play' : 'In progress…'}
            </button>
          )}
        </div>
      </section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const PageHeader: React.FC = () => (
  <section className="relative pt-24 pb-10 px-4 sm:px-8 lg:px-16">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(245,166,35,0.08)_0%,transparent_50%)]" />
    <div className="max-w-7xl mx-auto relative text-center">
      <span className="text-amber-500 text-sm font-semibold tracking-widest uppercase mb-3 block">
        Rhythm Challenges
      </span>
      <h1 className="font-display text-4xl lg:text-5xl font-bold text-stone-100 mb-3">
        African <span className="italic text-amber-500">Drums</span>
      </h1>
      <p className="text-stone-400 max-w-2xl mx-auto">
        Listen to the phrase, then play it back in time. Ten challenges for each
        of the twelve instruments.
      </p>
      <div className="flex items-center justify-center gap-6 mt-5 text-sm">
        <Link to="/drum-soundboard" className="text-stone-500 hover:text-amber-500 transition-colors">
          Soundboard
        </Link>
        <Link to="/drum-leaderboard" className="text-stone-500 hover:text-amber-500 transition-colors">
          Drum Leaderboard
        </Link>
      </div>
    </div>
  </section>
);

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <p className="font-display text-2xl font-bold text-stone-100">{value}</p>
    <p className="text-stone-500 text-xs uppercase tracking-wider">{label}</p>
  </div>
);

interface StatusBannerProps {
  phase: string;
  countdownBeat: number;
  isLoading: boolean;
  loadedCount: number;
  totalCount: number;
  error: string | null;
}

const StatusBanner: React.FC<StatusBannerProps> = ({
  phase,
  countdownBeat,
  isLoading,
  loadedCount,
  totalCount,
  error,
}) => {
  if (error) {
    return (
      <p className="text-center text-coral-400 py-3">
        Could not load the drum samples: {error}
      </p>
    );
  }
  if (isLoading) {
    return (
      <p className="text-center text-stone-400 py-3 font-mono text-sm">
        Loading samples… {loadedCount}/{totalCount}
      </p>
    );
  }

  const message: Record<string, string> = {
    idle: 'Press Listen & Play to begin.',
    listening: '♪ Listen…',
    playing: 'Your turn — play it back!',
    finished: '',
  };

  if (phase === 'countdown') {
    return (
      <p className="text-center font-display text-4xl font-bold text-amber-500 py-2">
        {countdownBeat}
      </p>
    );
  }

  return (
    <p className="text-center text-stone-400 py-3">{message[phase] ?? ''}</p>
  );
};

interface ResultPanelProps {
  result: DrumRunStats & { accuracy: number; grade: string };
  saveState: SaveState;
  challengeId: string;
  onRetry: () => void;
}

const SAVE_MESSAGE: Record<SaveState, string> = {
  idle: '',
  saving: 'Saving your score…',
  saved: 'Saved to the leaderboard.',
  failed: 'Could not save your score — your result still stands.',
  'signed-out': 'Sign in to save your score to the leaderboard.',
};

const ResultPanel: React.FC<ResultPanelProps> = ({
  result,
  saveState,
  challengeId,
  onRetry,
}) => (
  <div className="bg-studio-900/60 border border-studio-800 rounded-2xl p-6 text-center">
    <p className="text-stone-500 text-xs uppercase tracking-widest mb-2">
      Challenge complete
    </p>
    <p
      className="font-display text-6xl font-bold mb-1"
      style={{ color: GRADE_COLORS[result.grade] ?? NEUTRAL }}
    >
      {result.grade}
    </p>
    <p className="font-display text-3xl font-bold text-stone-100">
      {result.score.toLocaleString()}
    </p>
    <p className="text-stone-400 text-sm mb-5">
      {result.accuracy.toFixed(1)}% accuracy · {result.maxCombo}x best combo
    </p>

    <div className="flex justify-center gap-6 text-sm mb-6">
      <span className="text-sage-400">{result.perfectCount} perfect</span>
      <span className="text-amber-500">{result.goodCount} good</span>
      <span className="text-coral-400">{result.missCount} missed</span>
    </div>

    {SAVE_MESSAGE[saveState] && (
      <p className="text-stone-500 text-xs mb-4">{SAVE_MESSAGE[saveState]}</p>
    )}

    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <button type="button" onClick={onRetry} className="btn-primary">
        Try Again
      </button>
      <Link
        to={`/drum-leaderboard?challenge=${challengeId}`}
        className="btn-secondary"
      >
        View Leaderboard
      </Link>
    </div>
  </div>
);
